import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Send, Loader2, Sparkles, User, Paperclip, X, CheckCircle2, ChevronDown, ChevronUp, LogOut, FileText, Briefcase, Home, Users } from 'lucide-react';
import { chatService } from '../services/chatService';
import apiClient from '../services/authService';

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmt$$(n) {
  if (!n && n !== 0) return null;
  return `$${Number(n).toLocaleString()}`;
}

function maskSsn(ssn) {
  if (!ssn) return null;
  const digits = ssn.replace(/\D/g, '');
  return digits.length >= 4 ? `●●●-●●-${digits.slice(-4)}` : '●●●-●●-●●●●';
}

// ── ApplicationSummaryPanel ───────────────────────────────────────────────────

function SectionRow({ icon: Icon, label, done, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-gray-100 last:border-0">
      <button
        type="button"
        onClick={() => done && setOpen(v => !v)}
        className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${done ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-default'}`}
      >
        <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${done ? 'bg-[#1D9E75]' : 'bg-gray-100'}`}>
          {done
            ? <CheckCircle2 className="w-3.5 h-3.5 text-white" />
            : <Icon className="w-3.5 h-3.5 text-gray-400" />
          }
        </div>
        <span className={`flex-1 text-sm font-medium ${done ? 'text-gray-800' : 'text-gray-400'}`}>{label}</span>
        {done && (
          open
            ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
            : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
        )}
        {!done && <span className="text-[11px] text-gray-400">Not yet collected</span>}
      </button>
      {done && open && (
        <div className="px-4 pb-3 pt-0 space-y-1 bg-gray-50">
          {children}
        </div>
      )}
    </div>
  );
}

function Field({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 text-xs">
      <span className="text-gray-400 w-28 flex-shrink-0">{label}</span>
      <span className="text-gray-700 font-medium">{value}</span>
    </div>
  );
}

function ApplicationSummaryPanel({ application, panelOpen, onToggle }) {
  if (!application) return null;

  const completed = application.completedSections || [];
  const pii       = application.pii || {};
  const dl        = application.driversLicense || {};
  const inc       = application.income || {};
  const history   = application.rentalHistory || [];
  const refs      = application.references || [];
  const incomeDocs = application.incomeDocuments || [];

  const totalSections = 5;
  const completedCount = completed.length;

  return (
    <div className="bg-white border-b border-gray-200 shadow-sm">
      {/* Panel header — always visible, toggles detail */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
      >
        {/* Mini progress dots */}
        <div className="flex gap-1">
          {Array.from({ length: totalSections }).map((_, i) => (
            <div
              key={i}
              className={`w-2 h-2 rounded-full transition-colors ${i < completedCount ? 'bg-[#1D9E75]' : 'bg-gray-200'}`}
            />
          ))}
        </div>
        <span className="flex-1 text-sm font-medium text-gray-700 text-left">
          Application Summary
          <span className="ml-2 text-xs font-normal text-gray-400">
            {completedCount}/{totalSections} sections complete
          </span>
        </span>
        {panelOpen
          ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
          : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
        }
      </button>

      {/* Expanded detail */}
      {panelOpen && (
        <div className="divide-y divide-gray-100">

          {/* Personal Info */}
          <SectionRow icon={User} label="Personal Info" done={completed.includes('pii')}>
            <Field label="Name"    value={[pii.firstName, pii.lastName].filter(Boolean).join(' ')} />
            <Field label="Phone"   value={pii.phone} />
            <Field label="Address" value={pii.currentAddress} />
            <Field label="DOB"     value={pii.dob} />
            {pii.ssn && <Field label="SSN" value={maskSsn(pii.ssn)} />}
          </SectionRow>

          {/* Driver's License / ID */}
          <SectionRow icon={FileText} label="ID / Driver's License" done={completed.includes('driversLicense')}>
            <Field label="State"   value={dl.issuingState} />
            <Field label="Expires" value={dl.expiryDate} />
            {dl.licenseNumber && <Field label="License #" value="Provided ✓" />}
            {dl.documentUrl && <Field label="Document" value="Uploaded ✓" />}
          </SectionRow>

          {/* Employment */}
          <SectionRow icon={Briefcase} label="Employment & Income" done={completed.includes('income')}>
            <Field label="Employer" value={inc.employer} />
            <Field label="Title"    value={inc.jobTitle} />
            <Field label="Type"     value={inc.employmentType} />
            <Field label="Annual"   value={fmt$$(inc.annualIncome)} />
            {incomeDocs.length > 0 && (
              <Field label="Income docs" value={`${incomeDocs.length} uploaded`} />
            )}
          </SectionRow>

          {/* Rental History */}
          <SectionRow icon={Home} label="Rental History" done={completed.includes('rentalHistory')}>
            {history.length === 0
              ? <p className="text-xs text-gray-500">First-time renter (no prior history)</p>
              : history.map((h, i) => (
                  <div key={i} className="text-xs text-gray-700 py-1 border-t border-gray-100 first:border-0">
                    <p className="font-medium">{h.address}</p>
                    <p className="text-gray-400">{h.landlordName}{h.monthlyRent ? ` · ${fmt$$(h.monthlyRent)}/mo` : ''}</p>
                  </div>
                ))
            }
          </SectionRow>

          {/* References */}
          <SectionRow icon={Users} label="References" done={completed.includes('references')}>
            {refs.map((r, i) => (
              <div key={i} className="text-xs text-gray-700 py-1 border-t border-gray-100 first:border-0">
                <p className="font-medium">{r.name}</p>
                <p className="text-gray-400">{r.relationship}{r.phone ? ` · ${r.phone}` : ''}</p>
              </div>
            ))}
          </SectionRow>

        </div>
      )}
    </div>
  );
}

const THINKING_LABELS = [
  'Reviewing your application…',
  'Saving your information…',
  'One moment…',
  'Processing…',
];

const ThinkingBubble = () => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIdx(i => (i + 1) % THINKING_LABELS.length), 2000);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-1">
        {[0, 150, 300].map(delay => (
          <div key={delay} className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: `${delay}ms` }} />
        ))}
      </div>
      <span className="text-[12px] text-gray-500">{THINKING_LABELS[idx]}</span>
    </div>
  );
};

export default function ApplyChat({ user, onLogout }) {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [application, setApplication] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const userMenuRef = useRef(null);
  const prevCompletedCount = useRef(0);
  const [sessionId] = useState(() => {
    const stored = sessionStorage.getItem('apply_session_id');
    if (stored) return stored;
    const id = `app-${Date.now()}`;
    sessionStorage.setItem('apply_session_id', id);
    return id;
  });
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!user || user.userType !== 'Applicant') {
      navigate('/apply');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    loadApplication();
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Auto-expand summary panel when a new section is completed
  useEffect(() => {
    const count = application?.completedSections?.length || 0;
    if (count > prevCompletedCount.current) {
      setPanelOpen(true);
      prevCompletedCount.current = count;
    }
  }, [application?.completedSections?.length]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleLogout() {
    if (onLogout) onLogout();
    sessionStorage.removeItem('apply_session_id');
    navigate('/apply');
  }

  async function loadApplication() {
    try {
      const res = await apiClient.get('/api/applications/mine');
      setApplication(res.data);
    } catch {
      // No application yet — that's fine, it gets created on first message
    }
  }

  async function loadHistory() {
    try {
      const res = await chatService.getChatHistory(sessionId);
      if (res.messages?.length) {
        setMessages(res.messages.map(m => ({ role: m.role, content: m.content })));
      }
    } catch {
      // No history yet
    }
  }

  async function handleSend(e) {
    e?.preventDefault();
    const text = input.trim();
    if (!text && !pendingFile) return;
    if (loading || uploading) return;

    const userContent = pendingFile ? `${text} [Uploaded: ${pendingFile.name}]` : text;
    setMessages(prev => [...prev, { role: 'user', content: userContent }]);
    setInput('');
    setLoading(true);

    try {
      let fileInfo = null;

      // Upload file if pending
      if (pendingFile && application?._id) {
        setUploading(true);
        const docType = detectDocType(pendingFile.name, text);
        const urlRes = await apiClient.post('/api/applications/me/upload-url', {
          fileName: pendingFile.name,
          contentType: pendingFile.type,
          applicationId: application._id,
          docType,
        });

        if (urlRes.data.uploadUrl) {
          await fetch(urlRes.data.uploadUrl, {
            method: 'PUT',
            body: pendingFile,
            headers: { 'Content-Type': pendingFile.type },
          });
          fileInfo = {
            blobPath: urlRes.data.blobPath,
            fileName: pendingFile.name,
            docType,
          };
          // Register the doc with the application
          await apiClient.post(`/api/applications/agent/${application._id}/add-document`, {
            docType,
            fileUrl: urlRes.data.blobPath,
            fileName: pendingFile.name,
            fileType: pendingFile.type,
          });
        }
        setUploading(false);
        setPendingFile(null);
      }

      const messageToSend = fileInfo
        ? `${text} [Document uploaded: ${fileInfo.fileName} (${fileInfo.docType})]`
        : text;

      const res = await chatService.sendMessage(sessionId, messageToSend, user?._id);
      const aiContent = res.aiMessage?.content || '';
      setMessages(prev => [...prev, { role: 'assistant', content: aiContent }]);

      // Refresh application state after each message
      loadApplication();
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'I had trouble processing that. Please try again.'
      }]);
    } finally {
      setLoading(false);
      setUploading(false);
    }
  }

  function detectDocType(fileName, message) {
    const lowerMsg = (message || '').toLowerCase();
    const lowerName = fileName.toLowerCase();
    if (lowerMsg.includes('license') || lowerMsg.includes('dl') || lowerName.includes('license') || lowerName.includes('dl')) return 'dl';
    if (lowerMsg.includes('pay stub') || lowerMsg.includes('paystub') || lowerMsg.includes('tax') || lowerMsg.includes('income') || lowerMsg.includes('w2') || lowerMsg.includes('bank')) return 'income';
    return 'other';
  }

  function handleFileSelect(e) {
    const file = e.target.files[0];
    if (file) setPendingFile(file);
    e.target.value = '';
  }

  return (
    <div className="min-h-screen bg-[#f4f4f0] flex flex-col">
      {/* Header */}
      <header className="bg-[#1a1a2e] px-6 py-4 flex items-center justify-between">
        <Link to="/" className="text-white font-medium text-lg tracking-wide">Nivasapro.ai</Link>
        <div className="flex items-center gap-5">

          {/* User menu */}
          {user && (
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(v => !v)}
                className="flex items-center gap-1.5 text-sm text-gray-300 hover:text-white transition-colors py-1 px-2 rounded-lg hover:bg-white/10"
              >
                <User className="w-4 h-4" />
                <span>{user.email || user.name || user.username}</span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden z-50">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-xs text-gray-500 truncate">{user.email || user.username}</p>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors text-left"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign out
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </header>
      <div className="h-1 bg-[#1D9E75]" />

      {/* Property context bar */}
      {application?.property && (
        <div className="bg-white border-b border-gray-200 px-6 py-2.5 flex items-center gap-3">
          <CheckCircle2 className="w-4 h-4 text-[#1D9E75] flex-shrink-0" />
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Applying for:</span>
            <span className="text-sm font-medium text-gray-800">
              {application.property.name || application.property.address}
            </span>
          </div>
          {application.status === 'submitted' && (
            <span className="ml-auto text-xs bg-green-50 text-green-700 border border-green-200 rounded-full px-2.5 py-0.5 font-medium">
              Submitted
            </span>
          )}
        </div>
      )}

      {/* Application Summary Panel */}
      <ApplicationSummaryPanel
        application={application}
        panelOpen={panelOpen}
        onToggle={() => setPanelOpen(v => !v)}
      />

      {/* Messages */}
      <main className="flex-1 overflow-y-auto px-4 py-6 max-w-3xl mx-auto w-full">
        {messages.length === 0 && !loading && (
          <div className="flex flex-col items-center justify-center h-full py-16 text-center">
            <div className="w-14 h-14 bg-white rounded-full shadow-sm border border-gray-200 flex items-center justify-center mb-4">
              <Sparkles className="w-6 h-6 text-cyan-500" />
            </div>
            <h2 className="text-lg font-semibold text-gray-800 mb-2">Hi, I'm Niva</h2>
            <p className="text-sm text-gray-500 max-w-sm">
              I'll guide you through your rental application. Let's get started whenever you're ready.
            </p>
            <button
              onClick={() => { setInput('I\'m ready to start my application'); setTimeout(() => handleSend(), 0); }}
              className="mt-6 px-6 py-2.5 bg-[#1D9E75] text-white text-sm font-medium rounded-xl hover:bg-[#178a65] transition-colors"
            >
              Get Started
            </button>
          </div>
        )}

        <div className="space-y-4">
          {messages.map((msg, i) => (
            <div key={i} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium ${msg.role === 'user' ? 'bg-gray-400' : 'bg-[#1a1a2e]'}`}>
                {msg.role === 'user' ? <User className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
              </div>
              <div className={`max-w-[75%] px-4 py-3 rounded-2xl text-sm ${
                msg.role === 'user'
                  ? 'bg-[#1D9E75] text-white rounded-tr-sm'
                  : 'bg-white border border-gray-200 text-gray-800 rounded-tl-sm shadow-sm'
              }`}>
                {msg.role === 'assistant' ? (
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      p: ({ children }) => <p className="mb-2 last:mb-0 leading-relaxed">{children}</p>,
                      ul: ({ children }) => <ul className="list-disc pl-4 mb-2 space-y-1">{children}</ul>,
                      ol: ({ children }) => <ol className="list-decimal pl-4 mb-2 space-y-1">{children}</ol>,
                      li: ({ children }) => <li className="text-sm">{children}</li>,
                      strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                      table: ({ children }) => (
                        <div className="overflow-x-auto my-2">
                          <table className="text-xs border-collapse w-full">{children}</table>
                        </div>
                      ),
                      th: ({ children }) => <th className="bg-gray-100 px-3 py-1.5 text-left font-medium border border-gray-200">{children}</th>,
                      td: ({ children }) => <td className="px-3 py-1.5 border border-gray-200">{children}</td>,
                    }}
                  >
                    {msg.content}
                  </ReactMarkdown>
                ) : (
                  <span className="whitespace-pre-wrap">{msg.content}</span>
                )}
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#1a1a2e] flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
                <ThinkingBubble />
              </div>
            </div>
          )}
        </div>
        <div ref={messagesEndRef} />
      </main>

      {/* Input bar */}
      <div className="bg-white border-t border-gray-200 px-4 py-4">
        <div className="max-w-3xl mx-auto">
          {pendingFile && (
            <div className="flex items-center gap-2 mb-2 px-3 py-2 bg-cyan-50 border border-cyan-200 rounded-xl">
              <Paperclip className="w-4 h-4 text-cyan-600 flex-shrink-0" />
              <span className="text-xs text-cyan-700 flex-1 truncate">{pendingFile.name}</span>
              <button onClick={() => setPendingFile(null)} className="text-cyan-500 hover:text-cyan-700">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          <form onSubmit={handleSend} className="flex items-end gap-2">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex-shrink-0 p-2.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
              title="Upload document"
            >
              <Paperclip className="w-5 h-5" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              className="hidden"
              onChange={handleFileSelect}
            />
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder="Type your message…"
              rows={1}
              className="flex-1 resize-none px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              style={{ minHeight: '44px', maxHeight: '120px' }}
            />
            <button
              type="submit"
              disabled={loading || uploading || (!input.trim() && !pendingFile)}
              className="flex-shrink-0 p-2.5 bg-[#1D9E75] hover:bg-[#178a65] text-white rounded-xl transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {loading || uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            </button>
          </form>
          <p className="text-center text-[11px] text-gray-400 mt-2">
            Your information is stored securely and only shared with your prospective landlord.
          </p>
        </div>
      </div>
    </div>
  );
}
